import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useRequireApproved } from '../lib/auth';
import {
  useChecklists,
  useCreateInvite,
  useWorkspace,
  useWorkspaceInvites,
  useWorkspaceMembers,
} from '../lib/queries';
import { ORG_KINDS } from '../data/orgKinds';
import { PATHWAYS, SCENARIOS } from '../data/checklist';

export function WorkspaceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useRequireApproved();
  const workspaceQ = useWorkspace(id);
  const membersQ = useWorkspaceMembers(id);
  const checklistsQ = useChecklists(id);
  const invitesQ = useWorkspaceInvites(id);
  const createInvite = useCreateInvite();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteErr, setInviteErr] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  if (authLoading) {
    return <CenterText>Loading…</CenterText>;
  }
  if (!user) return null;

  if (workspaceQ.isLoading) {
    return <CenterText>Loading workspace…</CenterText>;
  }
  if (workspaceQ.error) {
    return (
      <CenterText>
        <span className="text-[#a02525]">
          {(workspaceQ.error as Error).message}
        </span>
      </CenterText>
    );
  }
  if (!workspaceQ.data) return <CenterText>Workspace not found.</CenterText>;

  const ws = workspaceQ.data;
  const kindLabel = ORG_KINDS.find((k) => k.id === ws.kind)?.label ?? ws.kind ?? '';
  const members = membersQ.data ?? [];
  const myMembership = members.find((m) => m.user_id === user.id);
  const isAdmin =
    myMembership?.role === 'owner' || myMembership?.role === 'admin';

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
      <Link
        to="/dashboard"
        className="font-label text-[11px] uppercase tracking-[0.15em] text-ink-muted hover:text-accent transition-colors inline-block mb-6"
      >
        ← all workspaces
      </Link>

      <div className="eyebrow mb-4">Workspace</div>
      <div className="flex items-baseline gap-4 mb-2 flex-wrap">
        <h1 className="editorial-h1">{ws.name}</h1>
        {myMembership && (
          <span className="font-label text-[10px] uppercase tracking-[0.18em] text-accent">
            you are {myMembership.role}
          </span>
        )}
      </div>
      <p className="editorial-deck mb-12">{kindLabel}</p>

      {/* ---------- Checklists ---------- */}
      <section className="mb-14">
        <div className="font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent border-b border-rule pb-3 mb-5 flex justify-between items-baseline">
          <span>Checklists</span>
          <Link
            to={`/workspace/${ws.id}/checklist/new`}
            className="text-ink-soft hover:text-accent font-normal font-editorial text-[12px] tracking-normal normal-case transition-colors"
          >
            + new checklist
          </Link>
        </div>

        {checklistsQ.isLoading ? (
          <div className="font-editorial italic text-ink-muted">Loading…</div>
        ) : (checklistsQ.data ?? []).length === 0 ? (
          <div className="border border-rule bg-paper-deep p-7">
            <h3 className="font-editorial text-[20px] font-medium text-ink mb-1">
              No checklists yet.
            </h3>
            <p className="font-editorial italic text-[15px] text-ink-soft mb-5 max-w-xl">
              Create your first CYBHI checklist — pick a pathway and scenarios,
              and the filtered checklist becomes editable with checkboxes
              everyone on the workspace can see and update.
            </p>
            <Link
              to={`/workspace/${ws.id}/checklist/new`}
              className="btn-text"
            >
              Create a checklist
            </Link>
          </div>
        ) : (
          <ul className="space-y-px bg-rule border border-rule">
            {(checklistsQ.data ?? []).map((c) => {
              const pwName = c.pathway
                ? PATHWAYS.find((p) => p.id === c.pathway)?.short ?? c.pathway
                : '—';
              const scenarioShorts = c.scenarios
                .map((s) => SCENARIOS.find((x) => x.id === s)?.short)
                .filter((x): x is string => !!x);
              return (
                <li key={c.id}>
                  <Link
                    to={`/workspace/${ws.id}/checklist/${c.id}`}
                    className="block bg-paper p-5 hover:bg-paper-deep transition-colors group"
                  >
                    <div className="flex justify-between items-baseline gap-4 flex-wrap mb-1">
                      <div className="font-editorial text-[19px] font-medium text-ink group-hover:text-accent transition-colors">
                        {c.name}
                      </div>
                      <div className="font-label text-[10px] uppercase tracking-[0.18em] text-ink-muted">
                        Updated {new Date(c.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="font-editorial italic text-[13px] text-ink-muted">
                      Pathway {c.pathway ?? '?'} · {pwName}
                      {c.sub_pathway_c && ` (${c.sub_pathway_c})`}
                      {scenarioShorts.length > 0 &&
                        ` — ${scenarioShorts.join(', ')}`}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ---------- Members ---------- */}
      <section className="mb-12">
        <div className="font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent border-b border-rule pb-3 mb-5">
          Members
        </div>
        {membersQ.isLoading ? (
          <div className="font-editorial italic text-ink-muted">Loading…</div>
        ) : (
          <ul className="space-y-px bg-rule border border-rule">
            {members.map((m) => (
              <li
                key={m.user_id}
                className="bg-paper p-4 flex justify-between items-baseline gap-4 flex-wrap"
              >
                <div>
                  <div className="font-editorial text-[16px] text-ink">
                    {m.full_name ?? '(no name)'}
                    {m.user_id === user.id && (
                      <span className="font-editorial italic text-ink-muted text-[13px] ml-2">
                        (you)
                      </span>
                    )}
                  </div>
                  <div className="font-editorial italic text-[12px] text-ink-muted mt-1">
                    Joined {new Date(m.joined_at).toLocaleDateString()}
                  </div>
                </div>
                <span className="font-label text-[10px] uppercase tracking-[0.18em] text-accent">
                  {m.role}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---------- Invites (owner/admin only) ---------- */}
      {isAdmin && (
        <section className="mb-12">
          <div className="font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent border-b border-rule pb-3 mb-5">
            Invite teammates
          </div>

          <div className="border border-rule bg-paper-deep p-5 mb-5">
            <p className="font-editorial italic text-[14px] text-ink-soft mb-4 max-w-xl">
              Enter a teammate's email and we'll generate a share link. Send it
              to them however you like — Slack, email, anywhere. They open it,
              sign in (or create an account), and they're in.
            </p>
            <div className="flex gap-3 items-stretch flex-wrap">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="teammate@example.com"
                className="flex-1 min-w-[220px] bg-paper border border-rule px-3 py-2 font-editorial text-[15px] text-ink focus:outline-none focus:ring-2 focus:ring-accent focus:ring-inset"
              />
              <button
                type="button"
                onClick={async () => {
                  setInviteErr(null);
                  if (!inviteEmail.includes('@')) {
                    setInviteErr('Enter a valid email.');
                    return;
                  }
                  try {
                    await createInvite.mutateAsync({
                      org_id: ws.id,
                      email: inviteEmail,
                    });
                    setInviteEmail('');
                  } catch (e) {
                    setInviteErr(e instanceof Error ? e.message : String(e));
                  }
                }}
                disabled={createInvite.isPending}
                className="btn-text disabled:opacity-40"
              >
                {createInvite.isPending ? 'Creating…' : 'Generate link'}
              </button>
            </div>
            {inviteErr && (
              <div className="mt-3 font-editorial italic text-[13px] text-[#a02525]">
                ⚠ {inviteErr}
              </div>
            )}
          </div>

          {invitesQ.isLoading ? (
            <div className="font-editorial italic text-ink-muted">Loading invites…</div>
          ) : (invitesQ.data ?? []).length === 0 ? (
            <p className="font-editorial italic text-[13px] text-ink-muted">
              No invites yet.
            </p>
          ) : (
            <ul className="space-y-px bg-rule border border-rule">
              {(invitesQ.data ?? []).map((inv) => {
                const url = `${window.location.origin}/invite/${inv.token}`;
                const expired = new Date(inv.expires_at).getTime() < Date.now();
                const status = inv.accepted_at
                  ? 'Accepted'
                  : expired
                    ? 'Expired'
                    : 'Pending';
                return (
                  <li key={inv.id} className="bg-paper p-4">
                    <div className="flex justify-between items-baseline gap-4 flex-wrap mb-2">
                      <div className="font-editorial text-[15px] text-ink">
                        {inv.email}
                        <span className="font-editorial italic text-[12px] text-ink-muted ml-2">
                          ({inv.role})
                        </span>
                      </div>
                      <span
                        className="font-label text-[10px] uppercase tracking-[0.18em]"
                        style={{
                          color: inv.accepted_at
                            ? 'var(--accent)'
                            : expired
                              ? '#9a8d7e'
                              : 'var(--ink-soft)',
                        }}
                      >
                        {status}
                      </span>
                    </div>
                    {!inv.accepted_at && !expired && (
                      <div className="flex gap-2 items-center flex-wrap">
                        <input
                          type="text"
                          readOnly
                          value={url}
                          onFocus={(e) => e.currentTarget.select()}
                          className="flex-1 min-w-[260px] bg-paper-deep border border-rule px-2 py-1.5 font-mono text-[11px] text-ink-soft"
                        />
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(url);
                              setCopiedToken(inv.token);
                              setTimeout(
                                () =>
                                  setCopiedToken((cur) =>
                                    cur === inv.token ? null : cur,
                                  ),
                                1800,
                              );
                            } catch {
                              setCopiedToken(null);
                            }
                          }}
                          className="font-label text-[10px] uppercase tracking-[0.18em] text-accent hover:text-ink transition-colors px-2 py-1.5 border border-rule hover:border-accent"
                        >
                          {copiedToken === inv.token ? '✓ Copied' : 'Copy link'}
                        </button>
                      </div>
                    )}
                    <div className="font-editorial italic text-[11px] text-ink-muted mt-2">
                      {inv.accepted_at
                        ? `Accepted ${new Date(inv.accepted_at).toLocaleDateString()}`
                        : expired
                          ? `Expired ${new Date(inv.expires_at).toLocaleDateString()}`
                          : `Expires ${new Date(inv.expires_at).toLocaleDateString()}`}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function CenterText({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center font-editorial italic text-ink-muted">
      {children}
    </div>
  );
}
